import { FC, useCallback, useState, useContext, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { ScreenContext } from 'react-components'
import { useTranslation } from '../../../../hooks/locale'
import { useRouting } from '../../../../hooks/routes'
import { useAppDispatch } from '../../../../redux/hooks'
import { getSelectedListings, getSelectedCount, getCanCompare, deselectListing } from '../../../../redux/bulk-inquiry'
import { BULK_INQUIRY } from '../../../../constants/search-result-listing'
import { buildBulkInquiryCheckoutUrl } from '../../../../utils/bulk-inquiry-utils'
import SearchRegionNodeTransport from '../../../../transports/search-region-node-transport'
import { useBulkInquiry } from './use-bulk-inquiry'
import CompareModal from './compare-modal'
import AnalyticsService from '../../../../services/analytics-service'
import { bulkInquiryPageCategories } from '../../../../services/analytics-service/categories'
import { bulkInquiryPageActions } from '../../../../services/analytics-service/actions'
import {
    TrayViewport,
    TrayAligner,
    SidebarSpacer,
    OuterBar,
    HeadingRow,
    Heading,
    HeaderControls,
    IconButton,
    CollapseChevron,
    CollapsedBar,
    CollapsedLabel,
    ExpandChevron,
    WhiteCard,
    SlotsRow,
    FilledSlot,
    SlotImage,
    RemoveButton,
    StyledCloseIcon,
    EmptySlot,
    Actions,
    CompareWrap,
    CompareTooltip,
    CompareButton,
    SendButton,
} from './bulk-inquiry-tray-styles'

interface Props {
    filter: string
    searchRegionNode: SearchRegionNodeTransport | null
}

const BulkInquiryTray: FC<Props> = ({ filter, searchRegionNode }) => {
    const { t } = useTranslation()
    const { r } = useRouting()
    const dispatch = useAppDispatch()
    const { isEnabled, isSelectMode } = useBulkInquiry()
    const { isMobile } = useContext(ScreenContext)
    const [isCompareOpen, setIsCompareOpen] = useState(false)
    const [isCollapsed, setIsCollapsed] = useState(false)

    const selectedListings = useSelector(getSelectedListings)
    const count = useSelector(getSelectedCount)
    const canCompare = useSelector(getCanCompare)

    const visible = isEnabled && isSelectMode && count > 0 && !isCompareOpen
    const emptySlotCount = Math.max(0, BULK_INQUIRY.MAX_SELECTION - selectedListings.length)
    const showCollapsed = isMobile && isCollapsed

    const isAtMaxSelection = count >= BULK_INQUIRY.MAX_SELECTION

    const prevCountRef = useRef(count)
    useEffect(() => {
        if (count > prevCountRef.current || count === 0 || isAtMaxSelection) {
            setIsCollapsed(false)
        }
        prevCountRef.current = count
    }, [count, isAtMaxSelection])

    useEffect(() => {
        if (!isMobile || !visible || isAtMaxSelection) {
            return
        }
        const handleScroll = () => setIsCollapsed(true)
        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [isMobile, visible, isAtMaxSelection])

    const handleRemove = useCallback(
        (id: string | number) => {
            AnalyticsService.trackEvent({
                category: bulkInquiryPageCategories.TRAY,
                action: bulkInquiryPageActions.LISTING_DESELECTED_FROM_TRAY,
                value: String(id),
            })
            dispatch(deselectListing(String(id)))
        },
        [dispatch],
    )

    // Pure navigation, no tracking baked in — shared with CompareModal's own send button, which fires its
    // own distinct event (SEND_INQUIRY_FROM_COMPARE_MODAL_CLICKED) before calling this.
    const navigateToCheckout = useCallback(() => {
        const listingIds = selectedListings.map(listing => String(listing.id))
        globalThis.location.href = buildBulkInquiryCheckoutUrl(r('checkout'), listingIds, filter, {
            arrival: searchRegionNode?.arrival,
            departure: searchRegionNode?.departure,
            adults: searchRegionNode?.adults,
            children: searchRegionNode?.children,
        })
    }, [selectedListings, filter, searchRegionNode, r])

    const handleTraySendInquiryClick = useCallback(() => {
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.TRAY,
            action: bulkInquiryPageActions.SEND_INQUIRY_FROM_TRAY_CLICKED,
            value: String(selectedListings.length),
        })
        navigateToCheckout()
    }, [selectedListings.length, navigateToCheckout])

    const handleCompare = useCallback(() => {
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.TRAY,
            action: bulkInquiryPageActions.COMPARE_CLICKED,
            value: String(count),
        })
        setIsCompareOpen(true)
    }, [count])

    const handleCloseCompare = useCallback(() => setIsCompareOpen(false), [])

    return (
        <>
            <TrayViewport $visible={visible} aria-hidden={!visible}>
                <TrayAligner>
                    <SidebarSpacer />
                    <OuterBar>
                        {showCollapsed ? (
                            <CollapsedBar
                                aria-label={t('bulkInquiry.expandTray')}
                                onClick={() => setIsCollapsed(false)}
                            >
                                <CollapsedLabel>{t('bulkInquiry.trayHeading')}</CollapsedLabel>
                                <ExpandChevron />
                            </CollapsedBar>
                        ) : (
                            <>
                                <HeadingRow>
                                    <Heading>{t('bulkInquiry.trayHeading')}</Heading>
                                    {isMobile && !isAtMaxSelection && (
                                        <HeaderControls>
                                            <IconButton
                                                aria-label={t('bulkInquiry.collapseTray')}
                                                onClick={() => setIsCollapsed(true)}
                                            >
                                                <CollapseChevron />
                                            </IconButton>
                                        </HeaderControls>
                                    )}
                                </HeadingRow>
                                <WhiteCard>
                                    <SlotsRow>
                                        {selectedListings.map(listing => (
                                            <FilledSlot key={listing.id}>
                                                <SlotImage src={listing.images?.[0]?.url} alt={listing.title ?? ''} />
                                                <RemoveButton
                                                    aria-label={t('bulkInquiry.removeProperty')}
                                                    onClick={() => handleRemove(listing.id)}
                                                >
                                                    <StyledCloseIcon />
                                                </RemoveButton>
                                            </FilledSlot>
                                        ))}
                                        {Array.from({ length: emptySlotCount }).map((_, index) => (
                                            <EmptySlot key={`empty-slot-${index}`} />
                                        ))}
                                    </SlotsRow>
                                    <Actions>
                                        <CompareWrap>
                                            <CompareButton disabled={!canCompare} onClick={handleCompare}>
                                                {t('bulkInquiry.compare')}
                                            </CompareButton>
                                            {!canCompare && (
                                                <CompareTooltip>{t('bulkInquiry.compareTooltip')}</CompareTooltip>
                                            )}
                                        </CompareWrap>
                                        <SendButton onClick={handleTraySendInquiryClick}>
                                            {t('bulkInquiry.sendInquiryToAll')}
                                        </SendButton>
                                    </Actions>
                                </WhiteCard>
                            </>
                        )}
                    </OuterBar>
                </TrayAligner>
            </TrayViewport>
            <CompareModal
                isOpen={isCompareOpen}
                onClose={handleCloseCompare}
                onSendInquiry={navigateToCheckout}
                searchRegionNode={searchRegionNode}
            />
        </>
    )
}

export default BulkInquiryTray
